/**
 * CountUp — the addictive animated number (Revolut / Duolingo style).
 *
 * Animates 0 → target with an ease-out curve so a stat "lands" with weight.
 * Respects `prefers-reduced-motion` (shows the final value instantly). Pure
 * presentation; no deps beyond React.
 */

import { useEffect, useRef, useState, type CSSProperties } from 'react';

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** Animate a value from 0 to `target`. Returns the current (eased) value. */
export function useCountUp(target: number, durationMs = 1200, delayMs = 0): number {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setValue(target);
      return;
    }
    let startTs: number | null = null;
    const tick = (ts: number) => {
      if (startTs === null) startTs = ts;
      const elapsed = ts - startTs - delayMs;
      if (elapsed < 0) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const p = Math.min(1, elapsed / durationMs);
      const eased = 1 - (1 - p) ** 3; // easeOutCubic
      setValue(target * eased);
      if (p < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setValue(target);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current);
    };
  }, [target, durationMs, delayMs]);

  return value;
}

export interface CountUpProps {
  /** The number to count up to. */
  target: number;
  /** Decimal places (e.g. 2 for money). */
  decimals?: number;
  /** Text before the number (e.g. `$` or `₪`). */
  prefix?: string;
  /** Text after the number. */
  suffix?: string;
  durationMs?: number;
  delayMs?: number;
  style?: CSSProperties;
}

/** A `<span>` whose number animates up to `target` on mount. */
export function CountUp({
  target,
  decimals = 0,
  prefix = '',
  suffix = '',
  durationMs = 1200,
  delayMs = 0,
  style,
}: CountUpProps) {
  const v = useCountUp(target, durationMs, delayMs);
  const shown =
    decimals > 0 ? v.toFixed(decimals) : Math.round(v).toLocaleString('en-US');
  return (
    <span style={style}>
      {prefix}
      {shown}
      {suffix}
    </span>
  );
}

export default CountUp;
